import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Card, ConnectWalletModal } from "../../components/UI";
import { Input } from "../../components/UI/Input";
import AnlogIcon from "../../assets/images/clock.svg";
import { tokens, SourceNetworks } from "../../constants";
import { TokenSelector } from "../../components/Shared/TokenSelector";
import { SourceNetworkSelector } from "@/components/Shared/SourceNetworkSelector";
import { debouncedFetchPrice } from "@/services";
import { useBridge } from "@/hooks";
import { StatusModal } from "@/components/Shared/StatusModal/StatusModal";
import { useBalance } from "@/contexts/Balance";
import { MoveRight } from "lucide-react";
import { useWallet } from "@/contexts";

const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

const HomePage = () => {
  const [isConnectWalletModalOpen, setIsConnectWalletModalOpen] =
    useState(false);
  const [selectedToken, setSelectedToken] = useState(tokens[0]);
  const [selectedSourceNetwork, setSelectedSourceNetwork] = useState(
    SourceNetworks[0]
  );
  const [recipientAddress, setRecipientAddress] = useState("");
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [showAddressValidation, setShowAddressValidation] = useState(false);
  const [analogPrice, setAnalogPrice] = useState(0);
  const [formError, setFormError] = useState("");

  const {
    handleBridge,
    statusModelState,
    setStatusModelState,
    value,
    setValue,
    setChain,
    isProcessing,
  } = useBridge();

  const { availableToBridge } = useBalance();
  const { selectedAccount } = useWallet();
  console.log(selectedAccount);

  useEffect(() => {
    debouncedFetchPrice(setAnalogPrice);
  }, []);

  const handleNetworkSelect = useCallback(
    (network: (typeof SourceNetworks)[0]) => {
      setSelectedSourceNetwork(network);
      setChain(network.name);
    },
    [setChain]
  );

  const handleAmountChange = useCallback(
    (newAmount: string) => {
      setValue(newAmount);
      const numericAmount = Number(newAmount);
      if (numericAmount > 0) {
        debouncedFetchPrice(setAnalogPrice);
      }
      if (formError) {
        setFormError("");
      }
    },
    [setValue, formError]
  );

  const handleMaxClick = useCallback(() => {
    const maxAmount = parseFloat(availableToBridge) / Math.pow(10, 12);
    const maxAmountStr =
      maxAmount > 100 ? maxAmount.toFixed(2) : maxAmount.toString();
    setValue(maxAmountStr);
    if (maxAmount > 0) {
      debouncedFetchPrice(setAnalogPrice);
    }
  }, [availableToBridge, setValue]);

  const handleBridgeSubmit = useCallback(async () => {
    setFormError("");

    const maxAmount = parseFloat(availableToBridge) / Math.pow(10, 12);

    if (!recipientAddress || !EVM_ADDRESS_REGEX.test(recipientAddress)) {
      setFormError("Invalid recipient address format");
      setShowAddressValidation(true);
      setIsAddressValid(false);
      return;
    }

    if (!value || Number(value) <= 0) {
      setFormError("Amount must be greater than 0");
      return;
    }

    if (Number(value) > maxAmount) {
      setFormError(`Amount exceeds available balance of ${maxAmount} ANLOG`);
      return;
    }

    try {
      await handleBridge(recipientAddress);
      setRecipientAddress("");
      setShowAddressValidation(false);
      setIsAddressValid(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Bridge transaction failed";
      setFormError(errorMessage);
    }
  }, [recipientAddress, value, availableToBridge, handleBridge]);

  const handleRecipientChange = useCallback(
    (newAddress: string) => {
      setRecipientAddress(newAddress);

      // Clear any existing errors
      if (formError) {
        setFormError("");
      }

      // Validate address format when user has typed enough characters
      if (newAddress.length === 0) {
        setShowAddressValidation(false);
        setIsAddressValid(false);
      } else if (newAddress.length >= 42) {
        // Full EVM address length
        const isValid = EVM_ADDRESS_REGEX.test(newAddress);
        setIsAddressValid(isValid);
        setShowAddressValidation(true);
      } else if (newAddress.length >= 40 && !newAddress.startsWith("0x")) {
        // Invalid format if 40+ chars but doesn't start with 0x
        setIsAddressValid(false);
        setShowAddressValidation(true);
      } else {
        setShowAddressValidation(false);
        setIsAddressValid(false);
      }
    },
    [formError]
  );

  const handleRecipientBlur = useCallback(() => {
    // Validate on blur if there's any input
    if (recipientAddress.length > 0) {
      const isValid = EVM_ADDRESS_REGEX.test(recipientAddress);
      setIsAddressValid(isValid);
      setShowAddressValidation(true);
    }
  }, [recipientAddress]);

  const usdValue = useMemo(() => {
    return (Number(value) * analogPrice).toFixed(2);
  }, [value, analogPrice]);

  const statusModalTitle = useMemo(() => {
    return statusModelState?.status === "finalized"
      ? "Bridging Successful!"
      : "Bridging In Progress";
  }, [statusModelState?.status]);
  return (
    <div className="flex flex-col h-[calc(100vh-75px)] flex-1 mt-[75px]">
      <div className="container mx-auto flex-1 h-full flex flex-col">
        <div className="flex flex-col border-x flex-1 h-full border-borderGlass relative after:h-full after:w-[1px] after:bg-borderGlass after:absolute after:top-0 after:-left-12 before:h-full before:w-[1px] before:bg-borderGlass before:absolute before:top-0 before:-right-12">
          <Card className="max-w-[720px] w-full m-auto">
            <div className="flex items-center gap-3">
              <h4 className="text-xl leading-7 mb-2 flex-1">Bridge</h4>
            </div>
            <p className="text-sm leading-5 opacity-[64%] mb-6">
              Bridge your ANLOG tokens from Timechain to Base -
              fast, secure and seamless.
            </p>

            <div className="flex flex-col gap-6">
              <div className="grid grid-cols-[1fr_52px_1fr] gap-4 items-center">
                <div className="flex flex-col gap-2">
                  <label className="text-xs text-white/40">From</label>
                  <div className="border border-dropdownBorder bg-dropdownBg w-full justify-start flex h-[52px] box-border items-center px-4 py-3.5 gap-2.5 text-sm cursor-pointer">
                    <div className="w-9 h-9 rounded-full bg-purpleDiagonal flex items-center justify-center">
                      <img src={AnlogIcon} alt="" className="w-5 h-5" />
                    </div>
                    Timechain
                  </div>
                </div>

                <MoveRight className=" w-full mt-7" />

                <SourceNetworkSelector
                  networks={SourceNetworks}
                  className="flex flex-col gap-2"
                  selectedNetwork={selectedSourceNetwork}
                  onNetworkSelect={handleNetworkSelect}
                  label="To"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs text-white/40" htmlFor="amount">
                  Amount to Bridge
                </label>
                <div className="flex border bg-dropdownBg border-dropdownBorder">
                  <div className="flex flex-col flex-1 relative border-r border-dropdownBorder p-4">
                    <Input
                      id="amount"
                      className="border-none w-[338px] appearance-none p-0"
                      placeholder="0"
                      variant="outlined"
                      type="number"
                      value={value}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      disabled={isProcessing}
                      onKeyPress={(e) =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault()
                      }
                      error={!!formError && formError.includes("Amount")}
                      helperText={formError.includes("Amount") ? formError : ""}
                    />
                    <div className="flex gap-2 items-center">
                      <div className="flex-1 text-xs text-white/40">
                        ${usdValue}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleMaxClick}
                        disabled={isProcessing}
                      >
                        MAX
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-1">
                    <TokenSelector
                      className="w-full"
                      tokens={tokens}
                      selectedToken={selectedToken}
                      onSelect={setSelectedToken}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Recipient Address"
                  placeholder="Paste an EVM address (0x…); example: 0x1234…ABCD"
                  variant="outlined"
                  value={recipientAddress}
                  onChange={(e) => handleRecipientChange(e.target.value)}
                  onBlur={handleRecipientBlur}
                  error={showAddressValidation && !isAddressValid}
                  helperText={
                    showAddressValidation && !isAddressValid ? (
                      "Invalid recipient address. Enter a Base (EVM) address. It must start with 0x and be 42 characters."
                    ) : showAddressValidation && isAddressValid ? (
                      <div className="flex items-center gap-1 text-green-500">
                        ✅ Ready to bridge
                      </div>
                    ) : (
                      ""
                    )
                  }
                  disabled={isProcessing}
                  className=" placeholder:text-xs"
                />
              </div>

              {selectedAccount ? (
                <Button
                  className="p-0"
                  variant="secondary"
                  size="lg"
                  onClick={handleBridgeSubmit}
                  disabled={
                    !!formError ||
                    isProcessing ||
                    (recipientAddress.length > 0 &&
                      showAddressValidation &&
                      !isAddressValid)
                  }
                >
                  {isProcessing ? "Processing..." : "Bridge Tokens"}
                </Button>
              ) : (
                <Button
                  className="p-0"
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsConnectWalletModalOpen(true)}
                >
                  Connect Wallet
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
      {statusModelState && (
        <StatusModal
          currentStep={statusModelState}
          isOpen
          title={statusModalTitle}
          onClose={() => setStatusModelState(null)}
        />
      )}
      <ConnectWalletModal
        isOpen={isConnectWalletModalOpen}
        onClose={() => setIsConnectWalletModalOpen(false)}
      />
    </div>
  );
};

export default HomePage;
